import { loadMaplibre } from "@app/maplibre.ts";
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import type { GeoJSONSource, LngLatBoundsLike, Map as MapLibreMap, StyleSpecification } from "maplibre-gl";
import type { Feature, FeatureCollection, LineString, MultiLineString, Point } from "geojson";
import { GlobeCanvas, type FlyToTarget } from "./GlobeCanvas.tsx";
import { prefersReducedMotion, slerp, type Globe, type LatLon } from "@app/globe/globe.ts";
import { useTheme } from "@app/state/theme.ts";
import { captureBridge } from "@app/capture/bridge.ts";

/**
 * Globo de juego.
 * - Con WebGL: MapLibre GL (proyección globo) con teselas vectoriales
 *   estáticas de Natural Earth 1:50m (scripts/build-tiles.mjs). Sin capas de
 *   texto ni iconos (spec globe-map).
 * - Sin WebGL o si MapLibre falla: globo en canvas 2D (GlobeCanvas).
 */
export interface MapGlobeHandle {
  zoomIn(): void;
  zoomOut(): void;
}

interface Props {
  guess: LatLon | null;
  answer: LatLon | null;
  /** Cambia para volver a la vista inicial (nueva pista). */
  resetKey: number;
  ariaLabel: string;
  onTap: (point: LatLon) => void;
  onEnter: () => void;
  /** La cámara ha terminado de encuadrar intento y respuesta. */
  onRevealSettled?: () => void;
  ref?: Ref<MapGlobeHandle>;
}

const INITIAL = { center: [-8, 34] as [number, number], zoom: 1.6 };

function hasWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

interface Tokens {
  ocean: string;
  land: string;
  coast: string;
  grat: string;
  guess: string;
  answer: string;
  glow: number;
  edge: string;
}

function readTokens(): Tokens {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  const light = cs.colorScheme.includes("light") && !cs.colorScheme.includes("dark");
  return {
    ocean: v("--g-ocean"),
    land: v("--g-land"),
    coast: v("--g-coast"),
    grat: v("--g-grat"),
    guess: v("--g-guess"),
    answer: v("--g-answer"),
    glow: parseFloat(v("--g-glow")) || 0,
    edge: light ? "#ffffff" : "#000000",
  };
}

function graticule(step = 15): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = [];
  for (let lon = -180; lon < 180; lon += step) {
    const coords: [number, number][] = [];
    for (let lat = -85; lat <= 85; lat += 5) coords.push([lon, lat]);
    features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } });
  }
  for (let lat = -75; lat <= 75; lat += step) {
    const coords: [number, number][] = [];
    for (let lon = -180; lon <= 180; lon += 5) coords.push([lon, lat]);
    features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } });
  }
  return { type: "FeatureCollection", features };
}

const empty: FeatureCollection = { type: "FeatureCollection", features: [] };

function pointFc(p: LatLon | null): FeatureCollection<Point> {
  return {
    type: "FeatureCollection",
    features: p ? [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [p.lon, p.lat] } }] : [],
  };
}

/** Arco de gran círculo entre intento y respuesta (partido en el antimeridiano). */
function arcFc(a: LatLon | null, b: LatLon | null): FeatureCollection<MultiLineString> {
  if (!a || !b) return { type: "FeatureCollection", features: [] };
  const segments: [number, number][][] = [[]];
  let prev: number | null = null;
  for (let i = 0; i <= 64; i += 1) {
    const p = slerp(a, b, i / 64);
    if (prev !== null && Math.abs(p.lon - prev) > 180) segments.push([]);
    segments[segments.length - 1]!.push([p.lon, p.lat]);
    prev = p.lon;
  }
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "MultiLineString", coordinates: segments } }],
  };
}

function baseStyle(t: Tokens): StyleSpecification {
  return {
    version: 8,
    projection: { type: "globe" },
    sources: {},
    layers: [{ id: "ocean", type: "background", paint: { "background-color": t.ocean } }],
  };
}

function applyPaint(map: MapLibreMap, t: Tokens): void {
  type Prop = Parameters<MapLibreMap["setPaintProperty"]>[1];
  type Value = Parameters<MapLibreMap["setPaintProperty"]>[2];
  const set = (layer: string, prop: Prop, value: Value) => {
    if (map.getLayer(layer)) map.setPaintProperty(layer, prop, value);
  };
  set("ocean", "background-color", t.ocean);
  set("graticule", "line-color", t.grat);
  set("land", "fill-color", t.land);
  set("borders", "line-color", t.coast);
  set("coast", "line-color", t.coast);
  set("arc-glow", "line-color", t.guess);
  set("arc-glow", "line-opacity", t.glow ? 0.45 : 0);
  set("arc", "line-color", t.guess);
  set("guess", "circle-color", t.guess);
  set("guess", "circle-stroke-color", t.edge);
  set("answer", "circle-color", t.answer);
  set("answer", "circle-stroke-color", t.edge);
}

export function MapGlobe({ guess, answer, resetKey, ariaLabel, onTap, onEnter, onRevealSettled, ref }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const fallbackRef = useRef<Globe | null>(null);
  const [mode, setMode] = useState<"loading" | "maplibre" | "canvas">(() => (hasWebGL() ? "loading" : "canvas"));
  const [styleReady, setStyleReady] = useState(false);
  const tapRef = useRef(onTap);
  const enterRef = useRef(onEnter);
  const settledRef = useRef(onRevealSettled);
  tapRef.current = onTap;
  enterRef.current = onEnter;
  settledRef.current = onRevealSettled;
  const resolvedTheme = useTheme((s) => s.resolved);

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      if (mapRef.current) mapRef.current.zoomIn();
      else fallbackRef.current?.setZoom(fallbackRef.current.zoom * 1.5);
    },
    zoomOut: () => {
      if (mapRef.current) mapRef.current.zoomOut();
      else fallbackRef.current?.setZoom(fallbackRef.current.zoom / 1.5);
    },
  }));

  // Creación del mapa (una vez).
  useEffect(() => {
    if (mode !== "loading") return;
    let cancelled = false;
    let observer: ResizeObserver | undefined;
    (async () => {
      try {
        const maplibregl = await loadMaplibre();
        if (cancelled || !containerRef.current) return;
        const tokens = readTokens();
        const map = new maplibregl.Map({
          container: containerRef.current,
          style: baseStyle(tokens),
          center: INITIAL.center,
          zoom: INITIAL.zoom,
          minZoom: 0,
          maxZoom: 9,
          attributionControl: false,
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
          clickTolerance: 6,
          renderWorldCopies: false,
          fadeDuration: 0,
        });
        map.touchZoomRotate.disableRotation();
        map.keyboard.disableRotation();
        mapRef.current = map;
        observer = new ResizeObserver(() => map.resize());
        observer.observe(containerRef.current);

        map.on("error", (e) => console.warn("[globo]", e.error?.message));
        // Señal observable (tests y diagnóstico): la tierra ya se ha procesado y pintado.
        map.on("sourcedata", (e) => {
          if (e.sourceId === "world" && map.isSourceLoaded("world")) {
            containerRef.current?.setAttribute("data-land", "loaded");
          }
        });
        map.on("load", () => {
          if (cancelled) return;
          map.addSource("graticule", { type: "geojson", data: graticule() });
          map.addSource("world", {
            type: "vector",
            tiles: [`${location.origin}/geo/tiles/{z}/{x}/{y}.pbf`],
            minzoom: 0,
            maxzoom: 6,
          });
          map.addSource("arc", { type: "geojson", data: empty });
          map.addSource("guess", { type: "geojson", data: empty });
          map.addSource("answer", { type: "geojson", data: empty });
          map.addLayer({ id: "graticule", type: "line", source: "graticule", paint: { "line-color": tokens.grat, "line-width": 0.8 } });
          map.addLayer({ id: "land", type: "fill", source: "world", "source-layer": "land", paint: { "fill-color": tokens.land } });
          map.addLayer({ id: "borders", type: "line", source: "world", "source-layer": "borders", paint: { "line-color": tokens.coast, "line-width": 0.6, "line-opacity": 0.55 } });
          map.addLayer({ id: "coast", type: "line", source: "world", "source-layer": "coast", paint: { "line-color": tokens.coast, "line-width": 1.1 } });
          map.addLayer({ id: "arc-glow", type: "line", source: "arc", paint: { "line-color": tokens.guess, "line-width": 8, "line-blur": 6, "line-opacity": tokens.glow ? 0.45 : 0 } });
          map.addLayer({ id: "arc", type: "line", source: "arc", layout: { "line-cap": "round" }, paint: { "line-color": tokens.guess, "line-width": 2.5 } });
          map.addLayer({ id: "guess", type: "circle", source: "guess", paint: { "circle-radius": 8, "circle-color": tokens.guess, "circle-stroke-width": 2, "circle-stroke-color": tokens.edge } });
          map.addLayer({ id: "answer", type: "circle", source: "answer", paint: { "circle-radius": 8, "circle-color": tokens.answer, "circle-stroke-width": 2.5, "circle-stroke-color": tokens.edge } });
          setMode("maplibre");
          setStyleReady(true);
          captureBridge.registerMap(map);
        });
        map.on("click", (e) => {
          const at = captureBridge.preciseTap(e.point) ?? e.lngLat;
          tapRef.current({ lat: at.lat, lon: ((at.lng + 540) % 360) - 180 });
        });
        const canvas = map.getCanvas();
        canvas.setAttribute("aria-label", ariaLabel);
        canvas.addEventListener("keydown", (e) => {
          if (e.key === " ") {
            e.preventDefault();
            const c = map.getCenter();
            tapRef.current({ lat: c.lat, lon: ((c.lng + 540) % 360) - 180 });
          } else if (e.key === "Enter") {
            enterRef.current();
          }
        });
      } catch (error) {
        console.warn("[globo] MapLibre no disponible, uso el globo en canvas", error);
        if (!cancelled) setMode("canvas");
      }
    })();
    return () => {
      cancelled = true;
      observer?.disconnect();
      if (mapRef.current) captureBridge.registerMap(null);
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // El mapa se crea una sola vez; los cambios llegan por los efectos de abajo.
  }, []);

  // Pines y arco.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    (map.getSource("guess") as GeoJSONSource | undefined)?.setData(pointFc(guess));
    (map.getSource("answer") as GeoJSONSource | undefined)?.setData(pointFc(answer));
    (map.getSource("arc") as GeoJSONSource | undefined)?.setData(arcFc(answer ? guess : null, answer));
  }, [guess, answer, styleReady]);

  // Encuadre de la revelación: intento + respuesta, dejando sitio a la hoja inferior.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady || !guess || !answer) return;
    const west = guess.lon;
    let east = answer.lon;
    if (Math.abs(east - west) > 180) east += east < west ? 360 : -360;
    const bounds: LngLatBoundsLike = [
      [Math.min(west, east), Math.min(guess.lat, answer.lat)],
      [Math.max(west, east), Math.max(guess.lat, answer.lat)],
    ];
    const h = map.getContainer().clientHeight;
    map.once("moveend", () => settledRef.current?.());
    map.fitBounds(bounds, {
      padding: { top: 60, left: 50, right: 50, bottom: Math.round(h * 0.45) },
      maxZoom: 7,
      duration: prefersReducedMotion() ? 0 : 1100,
    });
  }, [answer, guess, styleReady]);

  // Nueva pista: vuelta a la vista inicial.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    map.easeTo({ center: INITIAL.center, zoom: INITIAL.zoom, duration: prefersReducedMotion() ? 0 : 700 });
  }, [resetKey, styleReady]);

  // Cambio de tema claro/oscuro.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    applyPaint(map, readTokens());
  }, [resolvedTheme, styleReady]);

  if (mode === "canvas") {
    return (
      <CanvasFallback
        guess={guess}
        answer={answer}
        resetKey={resetKey}
        ariaLabel={ariaLabel}
        onTap={onTap}
        onEnter={onEnter}
        onReady={(g) => {
          fallbackRef.current = g;
        }}
      />
    );
  }
  return (
    <div
      ref={containerRef}
      className="maplibre-globe"
      data-ready={mode === "maplibre" ? "true" : "false"}
      aria-busy={mode === "loading"}
    />
  );
}

function CanvasFallback({
  guess,
  answer,
  resetKey,
  ariaLabel,
  onTap,
  onEnter,
  onReady,
}: Omit<Props, "ref"> & { onReady: (g: Globe) => void }) {
  const [flyTo, setFlyTo] = useState<FlyToTarget | null>(null);
  useEffect(() => {
    if (!guess || !answer) return;
    const mid = slerp(guess, answer, 0.5);
    const R = 6371;
    const dLat = ((answer.lat - guess.lat) * Math.PI) / 180;
    const dLon = ((answer.lon - guess.lon) * Math.PI) / 180;
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((guess.lat * Math.PI) / 180) * Math.cos((answer.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    const angle = (2 * R * Math.asin(Math.min(1, Math.sqrt(h)))) / R;
    setFlyTo({
      lat: mid.lat,
      lon: mid.lon,
      zoom: Math.max(1, Math.min(14, 0.55 / Math.sin(Math.min(1.3, angle / 2 + 0.02)))),
      offsetY: -0.16,
      key: Date.now(),
    });
  }, [guess, answer]);
  useEffect(() => {
    if (resetKey) setFlyTo({ lat: 34, lon: -8, zoom: 1, key: Date.now() });
  }, [resetKey]);
  return (
    <GlobeCanvas
      interactive
      fill={0.44}
      guess={guess}
      answer={answer}
      flyTo={flyTo}
      onTap={(p) => onTap(p)}
      onEnter={() => onEnter()}
      onReady={onReady}
      ariaLabel={ariaLabel}
    />
  );
}
